/**
 * Environment-Isolated LocalStorage Wrapper
 *
 * Automatically adds environment prefix to all localStorage keys to prevent
 * cross-environment data pollution (dev/staging/production).
 *
 * Usage:
 *   import { envStorage } from '@/lib/storage/envIsolatedStorage';
 *
 *   // Will actually store as "development-myKey" in dev environment
 *   envStorage.setItem('myKey', 'value');
 *   const value = envStorage.getItem('myKey');
 *
 * Note: This module uses native console instead of Logger to avoid circular dependencies
 * with @/utils (which imports this module for clearPersistedStores)
 */

/**
 * Get current environment name
 */
function getCurrentEnvironment(): string {
  return import.meta.env.VITE_ENV || "development";
}

/**
 * Add environment prefix to a key
 */
function getEnvKey(key: string): string {
  const env = getCurrentEnvironment();
  return `${env}-${key}`;
}

/**
 * Remove environment prefix from a key
 */
function removeEnvPrefix(envKey: string): string {
  const env = getCurrentEnvironment();
  const prefix = `${env}-`;
  if (envKey.startsWith(prefix)) {
    return envKey.slice(prefix.length);
  }
  return envKey;
}

/**
 * Check if a key belongs to current environment
 */
function isCurrentEnvKey(key: string): boolean {
  const env = getCurrentEnvironment();
  return key.startsWith(`${env}-`);
}

/**
 * Environment-isolated get/set/remove over one Web Storage area.
 * The area is resolved on every call and failures (private mode, quota, disabled storage) are
 * logged and reported through the return value instead of thrown.
 */
export function createEnvIsolatedStorage(areaName: "localStorage" | "sessionStorage") {
  const area = (): Storage => globalThis[areaName];

  return {
    getItem(key: string): string | null {
      try {
        return area().getItem(getEnvKey(key));
      } catch (error) {
        console.error(`[EnvStorage] Failed to get ${areaName} item: ${key}`, error);
        return null;
      }
    },

    setItem(key: string, value: string): boolean {
      try {
        area().setItem(getEnvKey(key), value);
        return true;
      } catch (error) {
        console.error(`[EnvStorage] Failed to set ${areaName} item: ${key}`, error);
        return false;
      }
    },

    removeItem(key: string): boolean {
      try {
        area().removeItem(getEnvKey(key));
        return true;
      } catch (error) {
        console.error(`[EnvStorage] Failed to remove ${areaName} item: ${key}`, error);
        return false;
      }
    },
  };
}

const localEnvStorage = createEnvIsolatedStorage("localStorage");

/**
 * Environment-isolated sessionStorage (get/set/remove only).
 */
export const sessionEnvStorage = createEnvIsolatedStorage("sessionStorage");

/**
 * Environment-isolated localStorage wrapper
 */
export const envStorage = {
  /**
   * Get an item from localStorage with environment isolation
   */
  getItem: localEnvStorage.getItem,

  /**
   * Set an item in localStorage with environment isolation
   */
  setItem: localEnvStorage.setItem,

  /**
   * Remove an item from localStorage with environment isolation
   */
  removeItem: localEnvStorage.removeItem,

  /**
   * Get and parse JSON from localStorage
   */
  getJSON<T = unknown>(key: string): T | null {
    try {
      const value = this.getItem(key);
      if (!value) return null;
      return JSON.parse(value) as T;
    } catch (error) {
      console.error(`[EnvStorage] Failed to get/parse localStorage JSON: ${key}`, error);
      return null;
    }
  },

  /**
   * Set JSON in localStorage
   */
  setJSON<T = unknown>(key: string, value: T): boolean {
    try {
      return this.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`[EnvStorage] Failed to set localStorage JSON: ${key}`, error);
      return false;
    }
  },

  /**
   * Get all keys for current environment
   */
  keys(): string[] {
    try {
      const allKeys = Object.keys(localStorage);
      return allKeys.filter((key) => isCurrentEnvKey(key)).map((key) => removeEnvPrefix(key));
    } catch (error) {
      console.error("[EnvStorage] Failed to get localStorage keys", error);
      return [];
    }
  },

  /**
   * Clear all items for current environment only
   */
  clear(): void {
    try {
      const env = getCurrentEnvironment();
      const keysToRemove = Object.keys(localStorage).filter((key) => key.startsWith(`${env}-`));

      for (const key of keysToRemove) {
        localStorage.removeItem(key);
      }

      console.info(`[EnvStorage] Cleared ${keysToRemove.length} items for environment: ${env}`);
    } catch (error) {
      console.error("[EnvStorage] Failed to clear localStorage", error);
    }
  },

  /**
   * Get the number of items for current environment
   */
  length(): number {
    return this.keys().length;
  },

  /**
   * Check if a key exists in current environment
   */
  hasItem(key: string): boolean {
    return this.getItem(key) !== null;
  },

  /**
   * Remove multiple items
   */
  removeMultiple(keys: string[]): void {
    for (const key of keys) {
      this.removeItem(key);
    }
  },

  /**
   * Get current environment name
   */
  getEnvironment(): string {
    return getCurrentEnvironment();
  },

  /**
   * Clean up data from other environments
   * WARNING: This will delete data from other environments!
   */
  cleanupOtherEnvironments(): number {
    try {
      const currentEnv = getCurrentEnvironment();
      const validEnvs = ["development", "test", "staging", "production"];
      const otherEnvs = validEnvs.filter((env) => env !== currentEnv);

      let cleanedCount = 0;
      const allKeys = Object.keys(localStorage);

      for (const key of allKeys) {
        // Check if key belongs to another environment
        for (const env of otherEnvs) {
          if (key.startsWith(`${env}-`)) {
            localStorage.removeItem(key);
            cleanedCount++;
            break;
          }
        }
      }

      if (cleanedCount > 0) {
        console.info(`[EnvStorage] Cleaned up ${cleanedCount} items from other environments`);
      }

      return cleanedCount;
    } catch (error) {
      console.error("[EnvStorage] Failed to cleanup other environments", error);
      return 0;
    }
  },

  /**
   * Get storage statistics
   */
  getStats() {
    const currentEnv = getCurrentEnvironment();
    const allKeys = Object.keys(localStorage);

    const currentEnvKeys = allKeys.filter((key) => key.startsWith(`${currentEnv}-`));
    const otherEnvKeys =
      allKeys.filter(
        (key) =>
          key.startsWith("development-") ||
          key.startsWith("test-") ||
          key.startsWith("staging-") ||
          key.startsWith("production-"),
      ).length - currentEnvKeys.length;

    return {
      environment: currentEnv,
      currentEnvironmentItems: currentEnvKeys.length,
      otherEnvironmentItems: otherEnvKeys,
      totalItems: allKeys.length,
    };
  },
};

/**
 * Create a scoped storage for a specific prefix
 * Useful for organizing related data
 *
 * @example
 * const authStorage = createScopedStorage('auth');
 * authStorage.setItem('token', 'xxx'); // Stores as "dev-auth:token"
 */
export function createScopedStorage(scope: string) {
  return {
    getItem: (key: string) => envStorage.getItem(`${scope}:${key}`),
    setItem: (key: string, value: string) => envStorage.setItem(`${scope}:${key}`, value),
    removeItem: (key: string) => envStorage.removeItem(`${scope}:${key}`),
    getJSON: <T = unknown>(key: string) => envStorage.getJSON<T>(`${scope}:${key}`),
    setJSON: <T = unknown>(key: string, value: T) => envStorage.setJSON(`${scope}:${key}`, value),
    clear: () => {
      const keys = envStorage.keys().filter((k) => k.startsWith(`${scope}:`));
      for (const k of keys) {
        envStorage.removeItem(k);
      }
    },
  };
}

// Export for backward compatibility
export default envStorage;
